/**
 * StructureEditor - Ketcher-based molecular structure editor with remote Indigo backend
 *
 * Uses Ketcher with RemoteStructServiceProvider for server-side chemistry
 * via the Indigo service at /api/v2/indigo.
 *
 * Based on Adroit's Ketcher integration.
 */

import React, { useRef, useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import apiClient from '../../api/client';

/**
 * Check if a molfile already has non-zero 2D coordinates (V2000 or V3000).
 * If any atom has |x| > 0.001 or |y| > 0.001, coords exist and layout() should be skipped.
 */
const molfileHas2DCoords = (molfile: string): boolean => {
  if (!molfile) return false;

  // V3000 format: atom lines are "M  V30 idx symbol x y z aam"
  if (molfile.includes('V3000') || molfile.includes('V30 BEGIN')) {
    const lines = molfile.split('\n');
    for (const line of lines) {
      const trimmed = line.trimStart();
      if (trimmed.startsWith('M  V30') && !trimmed.includes('BEGIN') && !trimmed.includes('END') && !trimmed.includes('COUNTS')) {
        const fields = trimmed.split(/\s+/);
        if (fields.length >= 7) {
          const x = parseFloat(fields[4]);
          const y = parseFloat(fields[5]);
          if (!isNaN(x) && !isNaN(y) && (Math.abs(x) > 0.001 || Math.abs(y) > 0.001)) return true;
        }
      }
    }
    return false;
  }

  // V2000 format
  if (!molfile.includes('V2000')) return false;
  const lines = molfile.split('\n');
  let countsLineIdx = -1;
  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    if (lines[i].includes('V2000')) {
      countsLineIdx = i;
      break;
    }
  }
  if (countsLineIdx < 0) return false;
  const atomCount = parseInt(lines[countsLineIdx].substring(0, 3).trim(), 10);
  if (isNaN(atomCount) || atomCount === 0) return false;
  for (let i = countsLineIdx + 1; i < countsLineIdx + 1 + atomCount && i < lines.length; i++) {
    const line = lines[i];
    if (line.length < 30) continue;
    const x = parseFloat(line.substring(0, 10).trim());
    const y = parseFloat(line.substring(10, 20).trim());
    if (Math.abs(x) > 0.001 || Math.abs(y) > 0.001) return true;
  }
  return false;
};

// Lazy load Editor with RemoteStructServiceProvider connecting to Indigo backend
// CSS imported inside Promise.all alongside editor modules
const KetcherEditor = lazy(() =>
  Promise.all([
    import('ketcher-react'),
    import('ketcher-core'),
    import('ketcher-react/dist/index.css'),
  ]).then(([ketcherReact, ketcherCore]) => ({
    default: function LazyKetcherEditor({ onInit }: { onInit: (ketcher: any) => void }) {
      const { Editor } = ketcherReact;
      const { RemoteStructServiceProvider } = ketcherCore;

      // Fresh provider instance each time component mounts - connects to Indigo backend
      // RemoteStructServiceProvider appends /indigo/* automatically, so base is /api/v2
      const structServiceProvider = new RemoteStructServiceProvider('/api/v2/');

      return (
        <>
          <style>{`
            .MuiPopover-root { z-index: 100000 !important; }
          `}</style>
          <Editor
            staticResourcesUrl=""
            structServiceProvider={structServiceProvider}
            onInit={onInit}
            errorHandler={(message) => console.error('[Ketcher Error]', message)}
          />
        </>
      );
    },
  }))
);

/**
 * StructureEditorModal - Modal for viewing/editing molecular structures
 *
 * Modal for viewing/editing molecular structures via Ketcher.
 */
interface StructureEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (molfile: string, smiles: string, format?: 'mol' | 'ket' | 'helm') => void;
  initialStructure?: string;
  title?: string;
  moleculeName?: string;
}

export const StructureEditorModal: React.FC<StructureEditorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialStructure = '',
  title = 'Structure Viewer',
  moleculeName,
}) => {
  const ketcherRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [structureLoading, setStructureLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  // Hydrogen toggle: display-only, never modifies the underlying molecule
  const [showHydrogens, setShowHydrogens] = useState(false);
  const preHydrogenMolRef = useRef<string | null>(null); // stores molfile before H were added

  // Plain async function, fire-and-forget loadStructure
  const handleKetcherInit = async (ketcher: any) => {
    console.log('[Ketcher] onInit called, ketcher:', !!ketcher);
    ketcherRef.current = ketcher;
    setLoading(false);

    // Monkey-patch: suppress visual "swoosh" during server transforms.
    const patchRenderToSuppressFlash = (editor: any) => {
      try {
        const render = editor?.render;
        if (!render || render.__patchedNoFlash) return;
        render.__patchedNoFlash = true;
        const origSetMolecule = render.setMolecule.bind(render);
        render.setMolecule = function (struct: any, forceUpdateWithTimeout?: boolean) {
          const svg = render.paper?.canvas;
          if (svg) svg.style.visibility = 'hidden';
          origSetMolecule(struct, forceUpdateWithTimeout);
          requestAnimationFrame(() => { if (svg) svg.style.visibility = ''; });
        };
      } catch (e) {
        console.warn('[StructureEditor] Failed to patch render:', e);
      }
    };

    // Apply patch immediately if editor is ready, or retry in background
    if (ketcher.editor) {
      patchRenderToSuppressFlash(ketcher.editor);
    } else {
      (async () => {
        for (let i = 0; i < 50; i++) {
          if (ketcher.editor) { patchRenderToSuppressFlash(ketcher.editor); break; }
          await new Promise(r => setTimeout(r, 100));
        }
      })();
    }

    if (initialStructure) {
      setStructureLoading(true);

      // Inject molecule name into molfile header (first line) for Ketcher's save dialog
      let structureToLoad = initialStructure;
      if (moleculeName && initialStructure.includes('V2000')) {
        const lines = initialStructure.split('\n');
        if (lines.length >= 4) {
          lines[0] = moleculeName;
          structureToLoad = lines.join('\n');
        }
      }

      // Wait for ketcher.editor to be available (onInit fires before internal init completes)
      const waitForEditor = async (maxAttempts = 50): Promise<boolean> => {
        for (let i = 0; i < maxAttempts; i++) {
          if (ketcher.editor) return true;
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        return false;
      };

      // Separate async function called without await (fire-and-forget)
      const loadStructure = async () => {
        try {
          const editorReady = await waitForEditor();
          if (editorReady) patchRenderToSuppressFlash(ketcher.editor);
          if (!editorReady) {
            throw new Error('Editor failed to initialize');
          }

          const hasCoords = molfileHas2DCoords(structureToLoad);
          console.log('[Ketcher] hasCoords:', hasCoords, 'loading molecule...');

          if (hasCoords) {
            // Molecule has 2D coords — load directly
            await ketcher.setMolecule(structureToLoad);
          } else {
            // No 2D coords — ask Indigo for layout first
            try {
              const layoutRes = await apiClient.post('/v2/indigo/layout', {
                struct: structureToLoad,
                output_format: 'mol',
              });
              if (layoutRes.data?.struct) {
                await ketcher.setMolecule(layoutRes.data.struct);
              } else {
                await ketcher.setMolecule(structureToLoad);
              }
            } catch (layoutErr) {
              console.warn('[Ketcher] Layout failed, loading raw:', layoutErr);
              await ketcher.setMolecule(structureToLoad);
            }
          }

          setStructureLoading(false);
        } catch (err) {
          console.error('[Ketcher] Failed to load structure:', err);
          setStructureLoading(false);
          setError('Failed to load molecule structure.');
        }
      };

      loadStructure(); // Fire-and-forget - don't await
    }
  };

  // Hydrogen toggle handler: show/hide without permanently modifying the molecule
  const handleToggleHydrogens = useCallback(async () => {
    const ketcher = ketcherRef.current;
    if (!ketcher) return;
    try {
      if (!showHydrogens) {
        // Turning ON: stash current molfile, then unfold H for display
        const currentMol = await ketcher.getMolfile();
        preHydrogenMolRef.current = currentMol;
        const res = await apiClient.post('/v2/indigo/convert_explicit_hydrogens', {
          struct: currentMol,
          mode: 'unfold',
          output_format: 'mol',
        });
        if (res.data?.struct) {
          // Unfold returns zero coordinates — regenerate 2D layout
          let molToLoad = res.data.struct;
          try {
            const layoutRes = await apiClient.post('/v2/indigo/layout', {
              struct: molToLoad,
              output_format: 'mol',
            });
            if (layoutRes.data?.struct) {
              molToLoad = layoutRes.data.struct;
            }
          } catch (layoutErr) {
            console.warn('[StructureEditor] Layout after H unfold failed, using raw:', layoutErr);
          }
          await ketcher.setMolecule(molToLoad);
        }
        setShowHydrogens(true);
      } else {
        // Turning OFF: restore the stashed molfile (before H were added)
        if (preHydrogenMolRef.current) {
          await ketcher.setMolecule(preHydrogenMolRef.current);
          preHydrogenMolRef.current = null;
        }
        setShowHydrogens(false);
      }
    } catch (err) {
      console.error('[StructureEditor] Hydrogen toggle error:', err);
    }
  }, [showHydrogens]);

  // Force new Ketcher instance when modal opens with new structure
  useEffect(() => {
    if (isOpen) {
      setEditorKey(prev => prev + 1);
      setLoading(true);
      setStructureLoading(false);
      setError(null);
      setShowHydrogens(false);
      preHydrogenMolRef.current = null;
    }
  }, [isOpen, initialStructure]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    if (!ketcherRef.current || !onSave) return;

    try {
      // If H are currently shown, save the original (pre-H) molfile so the
      // hydrogen toggle never leaks display-only state into saved data.
      const molfile = (showHydrogens && preHydrogenMolRef.current)
        ? preHydrogenMolRef.current
        : await ketcherRef.current.getMolfile();
      let smiles = '';
      try {
        smiles = await ketcherRef.current.getSmiles();
      } catch {
        console.warn('[StructureEditor] SMILES unavailable (macro mode?) — continuing with molfile only');
      }
      onSave(molfile, smiles, 'mol');
      onClose();
    } catch (molError) {
      // getMolfile failed — likely in macromolecule mode
      // Get KET and convert to molfile via Indigo so the backend
      // always receives a standard format (MOL/SMILES), never raw KET JSON.
      console.warn('[StructureEditor] Molfile unavailable, trying KET format...');
      try {
        const ket = await ketcherRef.current.getKet();
        // Convert KET → molfile via the existing Indigo convert endpoint
        try {
          const convertRes = await apiClient.post('/v2/indigo/convert', {
            struct: typeof ket === 'string' ? ket : JSON.stringify(ket),
            output_format: 'mol',
          });
          if (convertRes.data?.struct) {
            console.log('[StructureEditor] KET converted to molfile successfully');
            onSave(convertRes.data.struct, '', 'mol');
            onClose();
            return;
          }
        } catch (convertErr) {
          console.warn('[StructureEditor] KET-to-molfile conversion failed:', convertErr);
        }
        // Conversion failed — send KET as last resort for macromolecule workflows
        onSave(ket, '', 'ket');
        onClose();
      } catch {
        // Last resort: try HELM notation
        console.warn('[StructureEditor] KET unavailable, trying HELM...');
        try {
          const helm = await ketcherRef.current.getHelm();
          onSave(helm, '', 'helm');
          onClose();
        } catch (helmError) {
          console.error('[StructureEditor] Failed to get structure in any format:', helmError);
        }
      }
    }
  };

  if (!isOpen) return null;

  // Use portal to render at body level, escaping any parent stacking contexts
  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'var(--card-bg)',
          borderRadius: '8px',
          width: '95vw',
          height: '95vh',
          maxWidth: '1400px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid var(--card-border)',
          flexShrink: 0,
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>{title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={handleToggleHydrogens}
              disabled={loading || structureLoading}
              style={{
                padding: '4px 12px',
                fontSize: '13px',
                borderRadius: '4px',
                border: '1px solid',
                borderColor: showHydrogens ? '#2563eb' : 'var(--card-border)',
                background: showHydrogens ? '#eff6ff' : 'var(--hover-bg)',
                color: showHydrogens ? '#2563eb' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: 500,
              }}
              title={showHydrogens ? 'Hide explicit hydrogens' : 'Show explicit hydrogens'}
            >
              {showHydrogens ? 'H shown' : 'Show H'}
            </button>
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                lineHeight: 1,
                padding: '4px 8px',
              }}
            >
              &times;
            </button>
          </div>
        </div>

        {/* Editor container — contain:paint buffers all repaints, eliminating flash */}
        <div style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          minHeight: 0,
          contain: 'paint',
        }}>
          {error && (
            <div className="absolute top-2 left-2 right-2 z-10 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {structureLoading && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              color: 'var(--text-secondary)',
              fontSize: '14px',
            }}>
              Loading molecule...
            </div>
          )}

          <Suspense
            fallback={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ color: 'var(--text-secondary)' }}>Loading structure editor...</div>
              </div>
            }
          >
            <div style={{ width: '100%', height: '100%' }}>
              <KetcherEditor key={editorKey} onInit={handleKetcherInit} />
            </div>
          </Suspense>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
          padding: '12px 16px',
          borderTop: '1px solid var(--card-border)',
          flexShrink: 0,
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '8px 16px',
              color: 'var(--text-primary)',
              background: 'var(--hover-bg)',
              border: '1px solid var(--card-border)',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
          {onSave && (
            <button
              onClick={handleSave}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StructureEditorModal;
